/**
 * CetusEd Community Portal — Student Document List
 *
 * COMPLIANCE: IDEA §300.614 gives parents the right to inspect and review
 *   all education records. This component lists IEPs, 504 plans, evaluation
 *   reports, and other documents for the parent's linked students.
 *
 *   FERPA §1232g(a)(1) — Parents have the right to access their child's
 *   education records. The document list MUST show all finalized documents
 *   and MUST NOT show other students' documents.
 *
 * MIGRATION TARGET: Monolith S18 IEP subsystem generates documents but has
 *   no parent-facing view. This component provides the read-only parent view.
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useStudentDocuments } from '../../api/hooks';
import { Card } from '../../ui/components/Card';
import { LoadingSpinner } from '../../ui/components/LoadingSpinner';

export const DocumentList: React.FC = () => {
  const auth = useAuth();
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    auth.user?.linkedStudents[0]?.id || null,
  );

  const { data: documents, isLoading, error } = useStudentDocuments(selectedStudentId || '');

  const handleStudentChange = useCallback((studentId: string) => {
    setSelectedStudentId(studentId);
  }, []);

  if (!auth.user) return null;

  return (
    <div className="document-list-page">
      <h1>Student Documents</h1>

      {auth.user.linkedStudents.length > 1 && (
        <div className="student-selector">
          <label htmlFor="student-select">Select Student:</label>
          <select
            id="student-select"
            value={selectedStudentId || ''}
            onChange={(e) => handleStudentChange(e.target.value)}
          >
            {auth.user.linkedStudents.map((student) => (
              <option key={student.id} value={student.id}>
                {student.name} — {student.school}
              </option>
            ))}
          </select>
        </div>
      )}

      {isLoading && <LoadingSpinner />}

      {error && (
        <div className="error-message" role="alert">
          Unable to load documents. Please try again later.
        </div>
      )}

      {documents && documents.length === 0 && (
        <p className="empty-state">No documents available for this student.</p>
      )}

      {documents && documents.length > 0 && (
        <div className="document-grid">
          {documents.map((doc) => (
            <Link key={doc.id} to={`/documents/${doc.id}`} className="document-link">
              <Card>
                <div className="document-card-header">
                  <span className={`document-type-badge document-type--${doc.documentType}`}>
                    {doc.documentType.toUpperCase()}
                  </span>
                  <span className={`document-status document-status--${doc.status}`}>
                    {doc.status}
                  </span>
                </div>
                <h3 className="document-title">{doc.title}</h3>
                <p className="document-date">
                  Updated: {new Date(doc.updatedAt).toLocaleDateString()}
                </p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
